import { Box, Typography } from '@mui/material';
import { JSX, ReactNode } from 'react';
import clsx from 'clsx';

export interface PolicyCardSectionProps {
  /**
   * The title of the section.
   */
  title?: string;
  /**
   * The caption of the section.
   */
  caption?: string;
  /**
   * Flag to show the items inline (3 columns)
   * @default false
   */
  inlineItems?: boolean;
  /**
   * The number of columns to show the items in.
   */
  cols?: number;
  /**
   * Flag to show a divider between the items.
   * @default true
   */
  itemsDivider?: boolean;
  /**
   * The items of the section.
   */
  items: { name?: string; value: ReactNode; large?: boolean; className?: string }[];
  /**
   * Flag to show a top border.
   */
  topBorder?: boolean;
  /**
   * Flag to show a bottom border.
   */
  bottomBorder?: boolean;
  /**
   * Optional class name for the section.
   */
  className?: string;
}

/**
 * A section of a policy card. Contains a title, caption and a list of items.
 */
const PolicyCardSection = ({
  title,
  caption,
  items,
  cols = 2,
  className,
  itemsDivider = true,
  inlineItems = false,
  bottomBorder = false,
  topBorder = true,
}: PolicyCardSectionProps): JSX.Element => (
  <Box
    className={clsx(className, 'py-2')}
    borderBottom={bottomBorder ? 1 : 0}
    borderTop={topBorder ? 1 : 0}
    borderColor="common.borderGray"
  >
    {caption && (
      <Typography className="uppercase" color="text.secondary" variant="caption">
        {caption}
      </Typography>
    )}
    {title && (
      <Typography fontWeight={700} variant="h4" className="mb-2">
        {title}
      </Typography>
    )}
    <Box
      gridTemplateColumns={`repeat(${cols}, 1fr)`}
      className={clsx(
        inlineItems ? `grid gap-2 ${itemsDivider ? 'divide-x' : ''}` : itemsDivider && 'divide-y',
      )}
    >
      {items.map(({ name, value, className, large }, index) => (
        <Box
          key={name || index}
          className={clsx(
            inlineItems ? 'px-4 -ml-4' : 'flex space-x-2 justify-between py-1',
            className,
          )}
        >
          {name && (
            <Typography color="text.secondary" lineHeight={1.2}>
              {name}
            </Typography>
          )}
          <Box className={clsx(name && !inlineItems && 'text-right')}>
            {Array.isArray(value) ? (
              value.map((line) => (
                <Box key={line}>
                  <Typography fontWeight={700} lineHeight={1.2} variant={large ? 'h4' : 'body1'}>
                    {line}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography fontWeight={700} lineHeight={1.2} variant={large ? 'h5' : 'body1'}>
                {value}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

export default PolicyCardSection;
